export const SLEEP_TIMER_STORAGE_KEY = 'jusic_room_sleep_timer_v1';
const MINUTES = [15, 30, 60];

function storageFor(storage) {
    if (storage !== undefined) return storage;
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch (e) {
        return null;
    }
}

// A song ID alone cannot distinguish the same song being played again.
export function getSleepSongKey(music) {
    if (!music || music.id == null) return '';
    const id = String(music.id).trim();
    const pushTime = Number(music.pushTime);
    if (!id || id === 'placeholder' || !Number.isFinite(pushTime) || pushTime <= 0) return '';
    return JSON.stringify([String(music.source || 'qq').toLowerCase(), id, pushTime]);
}

function normalizeTimer(value) {
    if (!value || !['armed', 'expired'].includes(value.status)) return null;
    if (value.mode === 'deadline' && MINUTES.includes(value.minutes)
        && typeof value.deadline === 'number' && Number.isFinite(value.deadline) && value.deadline > 0) {
        return {mode: 'deadline', minutes: value.minutes, deadline: value.deadline, status: value.status};
    }
    if (value.mode === 'after-song' && typeof value.songKey === 'string' && value.songKey) {
        try {
            const song = JSON.parse(value.songKey);
            if (Array.isArray(song) && song.length === 3 && typeof song[0] === 'string'
                && typeof song[1] === 'string' && getSleepSongKey({source: song[0], id: song[1], pushTime: song[2]}) === value.songKey) {
                return {mode: 'after-song', songKey: value.songKey, status: value.status};
            }
        } catch (e) { /* Ignore invalid state instead of arming an unknown timer. */ }
    }
    return null;
}

export function createSleepTimer(selection, music, now = Date.now()) {
    if (selection === 'after-song') {
        const songKey = getSleepSongKey(music);
        return songKey ? {mode: 'after-song', songKey, status: 'armed'} : null;
    }
    if (!MINUTES.includes(selection) || !Number.isFinite(now) || now < 0) return null;
    return {mode: 'deadline', minutes: selection, deadline: now + selection * 60000, status: 'armed'};
}

export function remainingSleepTimerMs(timer, now = Date.now()) {
    if (!timer || timer.mode !== 'deadline') return null;
    if (timer.status === 'expired') return 0;
    return Math.max(0, timer.deadline - now);
}

// Leave currentSongKey undefined before the first server snapshot after refresh.
// Passing '' means the server explicitly reported that no song is playing.
// Only pass endedSongKey for a natural end, not an early media/stream failure.
export function isSleepTimerDue(timer, now = Date.now(), currentSongKey, endedSongKey) {
    if (!timer) return false;
    if (timer.status === 'expired') return true;
    if (timer.mode === 'deadline') return now >= timer.deadline;
    if (timer.mode === 'after-song') {
        return endedSongKey === timer.songKey
            || (typeof currentSongKey === 'string' && currentSongKey !== timer.songKey);
    }
    return false;
}

export function markSleepTimerExpired(timer) {
    const normalized = normalizeTimer(timer);
    return normalized ? {...normalized, status: 'expired'} : null;
}

export function loadSleepTimer(storage, now = Date.now()) {
    try {
        const target = storageFor(storage);
        const value = normalizeTimer(JSON.parse(target && target.getItem(SLEEP_TIMER_STORAGE_KEY) || 'null'));
        // An expired preference remains expired across refresh, until explicitly cleared.
        return value && value.mode === 'deadline' && now >= value.deadline ? markSleepTimerExpired(value) : value;
    } catch (e) {
        return null;
    }
}

export function saveSleepTimer(timer, storage) {
    try {
        const target = storageFor(storage);
        const value = normalizeTimer(timer);
        if (!target || !value) return false;
        target.setItem(SLEEP_TIMER_STORAGE_KEY, JSON.stringify(value));
        return true;
    } catch (e) {
        return false;
    }
}

export function clearSleepTimer(storage) {
    try {
        const target = storageFor(storage);
        if (!target) return false;
        target.removeItem(SLEEP_TIMER_STORAGE_KEY);
        return true;
    } catch (e) {
        return false;
    }
}
